/**
 * theme-custom (2026-08-20) — 사용자가 고른 색·모서리를 실제 화면에 입힌다.
 *
 * 방식: `tokens.css` 가 `:root[data-theme][data-mode]` 로 깔아 둔 변수 위에, 고른 값만
 * **루트 인라인 변수로 덮어쓴다**. 안 고른 것은 건드리지 않으므로 **아무것도 고르지 않은
 * 사람의 화면은 지금과 완전히 같다**. 되돌리기는 그 변수를 지우는 것으로 끝난다.
 *
 * 파생색(hover·active·연한 배경)은 저장하지 않는다 — 주색 하나에서 `color-mix` 로 만든다.
 * 사용자가 다섯 색을 다 고르게 하면 조합이 어긋나기 쉽고, 되돌릴 곳도 그만큼 늘어난다.
 *
 * 글자색만 계산이 필요하다: 고른 주색 위에 흰 글자가 읽히는지 검은 글자가 읽히는지는
 * 밝기로만 갈리므로 여기서 판정한다(대비를 눈으로 확인하지 않고 넘기면 버튼 글자가 사라진다).
 *
 * ★★ **어둡게에서는 «밝기 계열» 을 얹지 않는다** (2026-08-29 · 운영에서 잡았다).
 *
 *    이 값들은 **밝은 모드를 보고 고른 색**이다. 인라인 변수라 `tokens.css` 의
 *    `:root[data-theme][data-mode]` 를 **무조건 이긴다** — 그래서 회사 테마를 쓰는 회사에서
 *    「어둡게」를 켜면 배경만 어두워지고 **글자는 밝은 모드의 짙은 남색 그대로** 남아
 *    명암비 **1.14** 가 됐다(통합설정·근태·기준 설정·우리 조직 … 글자가 안 보인다).
 *    실측: `--text:#21324e` on `#1e293b`. 인라인을 걷어내면 `#f1f5f9` 로 제대로 나온다.
 *
 * ★ 그래서 가른다 — **색상(브랜드)은 회사가 정하고, 밝기는 모드가 정한다.**
 *   어둡게에서 얹는 것은 주색·강조·위험색·모서리뿐이고, 배경·표면·글자·테두리·아이콘은
 *   `tokens.css` 의 어두운 값이 그대로 선다.
 * ★ 모드는 나중에도 바뀌므로(`themeStore.setMode`) **모드가 바뀌면 다시 입혀야 한다** —
 *   `reapplyCustomThemeForMode()` 가 그 자리다.
 */

export type ButtonRadius = 'square' | 'round' | 'pill';

export interface CustomTheme {
  primary?: string | null;
  accent?: string | null;
  danger?: string | null;
  line?: string | null;
  surface?: string | null;
  /* 영역별 색 (2026-08-20) — 값이 없으면 그 영역은 지금까지의 색을 그대로 쓴다.
     칠해지는 자리는 styles/theme-surfaces.css 가 한 곳에 모아 두었다. */
  bg?: string | null;
  rail?: string | null;
  menu?: string | null;
  /* 영역 잉크 (2026-09-02) — 안 고르면 그 영역 «바탕의 밝기» 로 계산해 넣는다.
     서버에 아직 칸이 없어 지금은 늘 자동 계산이다(마이그레이션 0). */
  railText?: string | null;
  menuText?: string | null;
  rrail?: string | null;
  status?: string | null;
  table?: string | null;
  tableHead?: string | null;
  tableHeadText?: string | null;
  icon?: string | null;
  iconHover?: string | null;
  tint?: string | null;
  text?: string | null;
  textMuted?: string | null;
  buttonRadius?: ButtonRadius;
  transitionMs?: number;
}

/** 모서리 3종 — 버튼·입력은 그대로 따르되 카드는 과하지 않게 묶는다(알약 카드는 읽기 어렵다). */
const RADIUS_SCALE: Record<ButtonRadius, { sm: string; md: string; lg: string; xl: string }> = {
  square: { sm: '2px', md: '3px', lg: '5px', xl: '7px' },
  round: { sm: '0.25rem', md: '0.5rem', lg: '0.75rem', xl: '1rem' },   // tokens.css 기본값과 동일
  pill: { sm: '999px', md: '999px', lg: '16px', xl: '20px' },
};

const VARS = [
  '--primary', '--primary-hover', '--primary-active', '--primary-tint', '--primary-text',
  '--accent-purple', '--color-danger', '--border', '--border-strong', '--surface', '--surface-2',
  // 영역별(2026-08-20)
  '--bg', '--rail-bg', '--menu-bg', '--rrail-bg', '--status-bg',
  '--table-bg', '--table-head-bg', '--table-head-text', '--icon', '--icon-hover',
  // 영역 잉크(2026-09-02) — 바탕만 받고 글자색이 없어 짙은 레일에서 글자가 안 읽혔다
  '--rail-text', '--rail-text-muted', '--menu-text', '--menu-text-muted',
  '--text', '--text-muted',
  '--radius-sm', '--radius-md', '--radius-lg', '--radius-xl', '--theme-transition',
] as const;

/** 값이 있을 때만 얹는다 — 없는 항목은 지우기만 하므로 테마 기본값이 그대로 돌아온다. */
function put(root: HTMLElement, name: string, value: string | null | undefined): void {
  if (isHex(value)) root.style.setProperty(name, value);
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** #RRGGBB 만 통과 — 잘못된 값이 CSS 변수로 흘러들면 화면이 통째로 깨진다. */
export function isHex(v: unknown): v is string {
  return typeof v === 'string' && HEX.test(v);
}

/** 배경 위에 흰 글자가 읽히는지(어두운 배경) 판정 — WCAG 상대 휘도. */
export function readableTextOn(hex: string): string {
  const c = hex.slice(1);
  const rgb = [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16) / 255);
  const lin = rgb.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const L = 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
  return L > 0.55 ? '#0f172a' : '#ffffff';
}

/**
 * 화면에 입힌다. 값이 없는 항목은 **지우기만** 하므로 테마 기본값이 그대로 돌아온다.
 * 색이 바뀌는 순간에는 잠깐 전환을 걸어 주고(기본 350ms), 끝나면 뗀다 —
 * 전환을 상시로 걸어 두면 화면 전체가 늘 애니메이션 대상이 되어 무거워진다.
 */
/** 마지막으로 입힌 것 — 모드가 바뀌면 이걸로 다시 입힌다(밝기 계열의 가부가 달라진다). */
let lastTheme: CustomTheme | null = null;

/** 지금 화면이 어두운가 — `themeStore` 가 `data-mode` 에 light|dark 만 적는다(auto 는 resolve 됨). */
function isDarkNow(): boolean {
  return typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-mode') === 'dark';
}

/**
 * 모드가 바뀌었을 때 다시 입힌다 — **어둡게에서 밝기 계열을 떼어 내려면 다시 돌아야 한다.**
 * 전환 애니메이션은 걸지 않는다(모드 전환 자체가 이미 화면을 통째로 바꾼다).
 */
export function reapplyCustomThemeForMode(): void {
  if (typeof document === 'undefined') return;
  applyCustomTheme(lastTheme ? { ...lastTheme, transitionMs: 0 } : null);
}

export function applyCustomTheme(theme: CustomTheme | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const ms = theme?.transitionMs ?? 350;
  lastTheme = theme;
  /** 밝기 계열을 얹을 것인가 — 어둡게에서는 `tokens.css` 의 어두운 값이 이겨야 한다. */
  const light = !isDarkNow();

  if (ms > 0) {
    root.style.setProperty('--theme-transition', `${ms}ms`);
    root.setAttribute('data-theme-anim', '');
    window.setTimeout(() => root.removeAttribute('data-theme-anim'), ms + 60);
  }

  // 먼저 전부 비운다 — 방금 지운 항목이 남아 있으면 "되돌리기" 가 되지 않는다.
  for (const v of VARS) {
    if (v === '--theme-transition') continue;
    root.style.removeProperty(v);
  }
  if (!theme) return;

  if (isHex(theme.primary)) {
    const p = theme.primary;
    root.style.setProperty('--primary', p);
    root.style.setProperty('--primary-hover', `color-mix(in srgb, ${p} 84%, black)`);
    root.style.setProperty('--primary-active', `color-mix(in srgb, ${p} 70%, black)`);
    // ★ 선택 배경만 밝기 계열이다 — 흰색과 섞은 값이라 어둡게에서는 눈이 부시고 글자가 묻힌다.
    if (light) root.style.setProperty('--primary-tint', `color-mix(in srgb, ${p} 16%, white)`);
    root.style.setProperty('--primary-text', readableTextOn(p));
  }
  // 선택 배경(tint)을 직접 고른 경우에는 주색에서 만든 값을 덮어쓴다 — 사용자가 고른 것이 이긴다.
  if (light) put(root, '--primary-tint', theme.tint);

  if (isHex(theme.accent)) root.style.setProperty('--accent-purple', theme.accent);
  if (isHex(theme.danger)) root.style.setProperty('--color-danger', theme.danger);
  if (light && isHex(theme.line)) {
    root.style.setProperty('--border', theme.line);
    root.style.setProperty('--border-strong', `color-mix(in srgb, ${theme.line} 70%, black)`);
  }
  if (light && isHex(theme.surface)) {
    root.style.setProperty('--surface', theme.surface);
    root.style.setProperty('--surface-2', `color-mix(in srgb, ${theme.surface} 88%, black)`);
  }

  /* 영역별 색 — 각 변수는 theme-surfaces.css 가 `var(--X, 지금까지의 값)` 으로 읽는다.
     ★ 배경·표면을 어둡게 바꾸면 글자가 안 읽히므로, 글자색을 안 고른 사람에게는
     배경 밝기로 계산해 넣는다(주색 위 글자에 쓰는 규칙과 같다). */
  /* ★★ 여기부터가 «밝기 계열» — 어둡게에서는 통째로 건너뛴다(위 머리말 참조). */
  if (light) {
    put(root, '--bg', theme.bg);
    put(root, '--rail-bg', theme.rail);
    put(root, '--menu-bg', theme.menu);
    put(root, '--rrail-bg', theme.rrail);
    put(root, '--status-bg', theme.status);
    put(root, '--table-bg', theme.table);
    put(root, '--table-head-bg', theme.tableHead);
    put(root, '--table-head-text', theme.tableHeadText);
    put(root, '--icon', theme.icon);
    put(root, '--icon-hover', theme.iconHover);
    put(root, '--text', theme.text);
    put(root, '--text-muted', theme.textMuted);
    if (isHex(theme.bg) && !isHex(theme.text)) {
      root.style.setProperty('--text', readableTextOn(theme.bg));
    }

    /* ★ 영역은 «바탕만» 받고 잉크가 없었다 — 그래서 짙은 레일을 고르면 그 위 글자가
       `--text`(밝은 바탕 기준으로 계산된 짙은 색) 그대로라 안 읽혔다. hover 도 고정
       회색이라 밝은 덩어리로 떴다. 여기서 «그 영역 자신의 밝기» 로 잉크를 계산해 짝을
       채운다 — 새 알고리즘이 아니라 위와 같은 `readableTextOn` 이다.
       글자색을 직접 고른 사람은 건드리지 않는다. */
    /* ★ 잉크는 «진한 것» 과 «흐린 것» 이 짝이다. 진한 것만 넣으면 쉬는 상태의 메뉴 글자가
       `--text-muted` 자리에 `#0f172a` 로 들어가 통째로 무거워진다(2026-09-02 운영에서 확인). */
    const railInk = isHex(theme.railText) ? theme.railText
      : (isHex(theme.rail) ? readableTextOn(theme.rail) : null);
    if (railInk !== null && isHex(theme.rail)) {
      root.style.setProperty('--rail-text', railInk);
      root.style.setProperty('--rail-text-muted', `color-mix(in srgb, ${railInk} 70%, ${theme.rail})`);
    }
    const menuInk = isHex(theme.menuText) ? theme.menuText
      : (isHex(theme.menu) ? readableTextOn(theme.menu) : null);
    if (menuInk !== null && isHex(theme.menu)) {
      root.style.setProperty('--menu-text', menuInk);
      root.style.setProperty('--menu-text-muted', `color-mix(in srgb, ${menuInk} 70%, ${theme.menu})`);
    }
  }

  const radius = RADIUS_SCALE[theme.buttonRadius ?? 'round'];
  if (theme.buttonRadius && theme.buttonRadius !== 'round') {
    root.style.setProperty('--radius-sm', radius.sm);
    root.style.setProperty('--radius-md', radius.md);
    root.style.setProperty('--radius-lg', radius.lg);
    root.style.setProperty('--radius-xl', radius.xl);
  }
}

/** 서버 설정 → 이 모듈이 쓰는 모양. 저장 형식이 바뀌어도 화면은 이 함수만 본다. */
export function themeFromSettings(s: {
  themePrimary?: string | null; themeAccent?: string | null; themeDanger?: string | null;
  themeLine?: string | null; themeSurface?: string | null;
  themeBg?: string | null; themeRail?: string | null; themeMenu?: string | null;
  themeRailText?: string | null; themeMenuText?: string | null;
  themeRrail?: string | null; themeStatus?: string | null; themeTable?: string | null;
  themeTableHead?: string | null; themeTableHeadText?: string | null;
  themeIcon?: string | null; themeIconHover?: string | null; themeTint?: string | null;
  themeText?: string | null; themeTextMuted?: string | null;
  buttonRadius?: string; themeTransitionMs?: number;
} | null | undefined): CustomTheme | null {
  if (!s) return null;
  const radius = s.buttonRadius === 'square' || s.buttonRadius === 'pill' ? s.buttonRadius : 'round';
  return {
    primary: s.themePrimary ?? null,
    accent: s.themeAccent ?? null,
    danger: s.themeDanger ?? null,
    line: s.themeLine ?? null,
    surface: s.themeSurface ?? null,
    bg: s.themeBg ?? null,
    rail: s.themeRail ?? null,
    menu: s.themeMenu ?? null,
    railText: s.themeRailText ?? null,
    menuText: s.themeMenuText ?? null,
    rrail: s.themeRrail ?? null,
    status: s.themeStatus ?? null,
    table: s.themeTable ?? null,
    tableHead: s.themeTableHead ?? null,
    tableHeadText: s.themeTableHeadText ?? null,
    icon: s.themeIcon ?? null,
    iconHover: s.themeIconHover ?? null,
    tint: s.themeTint ?? null,
    text: s.themeText ?? null,
    textMuted: s.themeTextMuted ?? null,
    buttonRadius: radius,
    transitionMs: typeof s.themeTransitionMs === 'number' ? s.themeTransitionMs : 350,
  };
}
